/**
 * @file index.tsx
 *
 * @fileoverview Renders a simple wizard step.
 */
import { cva } from "class-variance-authority";
import React from "react";
import styles from "../wizard.module.scss";
import CheckDoubleIcon from "@jq-icons/CheckDouble";
import { motion, MotionProps } from "framer-motion";
// Define a new interface that extends MotionProps
type MotionSpanProps = MotionProps & React.HTMLAttributes<HTMLSpanElement>;
const CustomMotion = motion<MotionSpanProps>("span");

const wizardStep = cva([styles.wizardStep], {
  variants: {
    active: {
      true: styles.wizardStepActive,
    },
    current: {
      false: styles.notCurrentStep,
    },
  },
});

interface WizardStepSimpleProps {
  /**
   * If the step is the current step.
   *
   * @default false
   */
  selected?: boolean;
  /**
   * If the step should be checked.
   *
   * @default false
   */
  checked?: boolean;
  /**
   * The step text
   */
  text: string | React.ReactNode;
  /**
   * The number to show in the step.
   */
  index: number;
  /**
   * The check icon title.
   *
   * @default "Checked"
   */
  checkIconTitle?: string;
  /**
   * The flag indicating if the step
   * is current one in the wizard.
   *
   * @default false
   */
  current?: boolean;
}

export default function WizardStepSimple({
  index,
  text,
  selected = false,
  checked = false,
  current = false,
  checkIconTitle = "Checked",
}: WizardStepSimpleProps) {
  return (
    <div className={wizardStep({ active: selected, current })}>
      <CustomMotion
        className={styles.wizardStepNumber}
        animate={
          selected ? (checked ? "activeAndChecked" : "active") : "inactive"
        }
        variants={{
          active: {
            backgroundColor: "var(--wizard-step-number-active-bg)",
            borderColor: "var(--wizard-step-number-active-border-color)",
            color: "var(--wizard-step-number-active-color)",
          },
          inactive: {
            backgroundColor: "var(--wizard-step-number-upcoming-bg)",
            borderColor: "var(--wizard-step-number-upcoming-border-color)",
            color: "var(--wizard-step-number-upcoming-color)",
          },
          activeAndChecked: {
            backgroundColor: "var(--wizard-step-number-completed-bg)",
            borderColor: "var(--wizard-step-number-completed-border-color)",
            color: "var(--wizard-step-number-completed-color)",
          },
        }}
        transition={{ duration: 0.3 }}
      >
        <motion.span
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
          }}
          transition={{ duration: 0.4 }}
          key={checked.toString()}
        >
          {checked ? (
            <CheckDoubleIcon height="12" width="12" title={checkIconTitle} />
          ) : (
            index + 1
          )}
        </motion.span>
      </CustomMotion>
      <span>{text}</span>
    </div>
  );
}
export { WizardStepSimple };
